import { Chip } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMatch } from 'react-router';
import { getRouteTitle, ROUTES } from '../../../../../constants/RouteNameMapping';
import { PermissionKey } from '../../../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../../../services/PermissionService';
import { FeatureFlags, MaggieFeatureFlags } from '../../../../../util/feature-flags';
import { SubSectionNavigation } from '../../SubSectionNavigation/SubSectionNavigation';
import SubSectionNavItems from '../../SubSectionNavigation/SubSectionNavItems';
import { NavigationWrapper } from '../NavigationWrapper';
import { useKpiSourceHierarchyAvailable } from '../../../../../pages/AdminPanel/KpiSources/useKpiSourceHierarchyAvailable';
import { CONFIG_ROUTE } from './AdminPanelRoutesMappings';

function NewTag() {
  return (
    <Chip
      label='New'
      size='small'
      sx={(theme) => ({
        height: '1rem',
        fontSize: '0.625rem',
        backgroundColor: theme.colors.success[60],
        color: theme.colors.primary[0],
      })}
    />
  );
}

/**
 * "New" tag for recently shipped sections. Tied to the feature's flag existing
 * in code (not its LaunchDarkly value): the enum member's value is its name, so
 * it is undefined only once the flag has been deleted from FeatureFlags — and
 * removing the member also breaks this reference at compile time, so the tag
 * is cleaned up together with the flag.
 */
function newFeatureTag(flag: FeatureFlags | undefined) {
  if (flag === undefined) {
    return null;
  }
  return <NewTag />;
}

export const AdminPanelNavigation = () => {
  const flags = useFlags<MaggieFeatureFlags>();
  const match = useMatch(`/${ROUTES.ADMIN_PANEL}/*`);
  const permSvc = PermissionService.get();
  const kpiSourceHierarchyAvailable = useKpiSourceHierarchyAvailable();

  if (match === null) {
    return null;
  }

  // Trialed alongside the Field Manager rollout: alphabetical order within each
  // section when showFieldsManager is on, legacy hand-picked order otherwise.
  const orderSections = <T extends { value: string }>(sections: T[]) =>
    flags.showFieldsManager ? [...sections].sort((a, b) => a.value.localeCompare(b.value)) : sections;

  const MANAGEMENT_SECTIONS = [];
  if (flags.showAdminManageAccess) {
    MANAGEMENT_SECTIONS.push({
      id: ROUTES.ADMIN_PANEL_MANAGE_ACCESS,
      value: getRouteTitle(ROUTES.ADMIN_PANEL_MANAGE_ACCESS),
    });
  }
  MANAGEMENT_SECTIONS.push({
    id: ROUTES.ADMIN_PANEL_TEARSHEET_GENERATION,
    value: getRouteTitle(ROUTES.ADMIN_PANEL_TEARSHEET_GENERATION),
  });
  if (permSvc.hasPermission(PermissionKey.canBulkExportData)) {
    MANAGEMENT_SECTIONS.push({
      id: ROUTES.ADMIN_PANEL_TABLE_DOWNLOAD,
      value: getRouteTitle(ROUTES.ADMIN_PANEL_TABLE_DOWNLOAD),
    });
  }

  const CUSTOMIZATION_SECTIONS = [
    {
      id: ROUTES.ADMIN_PANEL_FIELD_MANAGER,
      value: getRouteTitle(ROUTES.ADMIN_PANEL_FIELD_MANAGER),
      RightSlot: newFeatureTag(FeatureFlags.showFieldsManager),
    },
    {
      id: ROUTES.ADMIN_PANEL_FIELDS,
      value: getRouteTitle(ROUTES.ADMIN_PANEL_FIELDS),
    },
    {
      id: ROUTES.ADMIN_PANEL_COLUMNS,
      value: getRouteTitle(ROUTES.ADMIN_PANEL_COLUMNS),
    },
    {
      id: ROUTES.ADMIN_PANEL_FORMATTERS,
      value: getRouteTitle(ROUTES.ADMIN_PANEL_FORMATTERS),
      RightSlot: newFeatureTag(FeatureFlags.showFormattersConfig),
    },
    {
      id: ROUTES.APPLICATION_SETTINGS,
      value: getRouteTitle(ROUTES.APPLICATION_SETTINGS),
    },
    {
      id: `${ROUTES.ADMIN_PANEL_DATA_DISPLAY}`,
      value: getRouteTitle(ROUTES.ADMIN_PANEL_DATA_DISPLAY),
    },
    {
      id: ROUTES.ADMIN_PANEL_MAPPINGS,
      value: getRouteTitle(ROUTES.ADMIN_PANEL_MAPPINGS),
    },
    {
      id: ROUTES.ADMIN_PANEL_ORG_PROFILE,
      value: getRouteTitle(ROUTES.ADMIN_PANEL_ORG_PROFILE),
    },
    {
      id: ROUTES.ADMIN_PANEL_RUBRICS,
      value: getRouteTitle(ROUTES.ADMIN_PANEL_RUBRICS),
    },
    {
      id: ROUTES.ADMIN_PANEL_DATA_COLLECTION,
      value: getRouteTitle(ROUTES.ADMIN_PANEL_DATA_COLLECTION),
    },
    ...(permSvc.hasPermission(PermissionKey.canViewClientSettings)
      ? [
          {
            id: ROUTES.ADMIN_PANEL_AUDITOR_CONFIG,
            value: getRouteTitle(ROUTES.ADMIN_PANEL_AUDITOR_CONFIG),
          },
          {
            id: ROUTES.ADMIN_PANEL_ROUND_CREATION,
            value: getRouteTitle(ROUTES.ADMIN_PANEL_ROUND_CREATION),
            // Shipped without a flag — the page is permission-gated instead — so nothing removes
            // this tag on its own. Drop it once customer success has had the page for a release.
            RightSlot: <NewTag />,
          },
        ]
      : []),
  ];

  const PIPELINE_SECTIONS = [
    {
      id: ROUTES.CONFIG_DEAL_TYPES,
      value: getRouteTitle(ROUTES.CONFIG_DEAL_TYPES),
    },
    {
      id: ROUTES.CONFIG_DEAL_STAGES,
      value: getRouteTitle(ROUTES.CONFIG_DEAL_STAGES),
    },
    {
      id: ROUTES.CONFIG_PASS_REASONS,
      value: getRouteTitle(ROUTES.CONFIG_PASS_REASONS),
    },
    {
      id: ROUTES.CONFIG_MISS_REASONS,
      value: getRouteTitle(ROUTES.CONFIG_MISS_REASONS),
    },
  ];

  const PORTFOLIO_SECTIONS = [
    {
      id: ROUTES.CONFIG_FUNDS,
      value: getRouteTitle(ROUTES.CONFIG_FUNDS),
    },
    {
      id: ROUTES.GENERAL_LEDGER,
      value: getRouteTitle(ROUTES.GENERAL_LEDGER),
    },
    {
      id: ROUTES.BULK_DEAL_DOCS,
      value: getRouteTitle(ROUTES.BULK_DEAL_DOCS),
    },
    {
      id: ROUTES.UPDATE_DEAL_DOCS_ROUND,
      value: getRouteTitle(ROUTES.UPDATE_DEAL_DOCS_ROUND),
    },
    {
      id: ROUTES.RESET_REINGESTION,
      value: getRouteTitle(ROUTES.RESET_REINGESTION),
    },
    {
      id: ROUTES.CONFIG_KPI,
      value: getRouteTitle(ROUTES.CONFIG_KPI),
    },
    {
      id: ROUTES.CONFIG_KPI_GROUPS,
      value: getRouteTitle(ROUTES.CONFIG_KPI_GROUPS),
    },
    ...(kpiSourceHierarchyAvailable
      ? [
          {
            id: ROUTES.CONFIG_KPI_SOURCES,
            value: getRouteTitle(ROUTES.CONFIG_KPI_SOURCES),
            RightSlot: newFeatureTag(FeatureFlags.showKpiSourceHierarchy),
          },
        ]
      : []),
    {
      id: ROUTES.CONFIG_FIELD_AUTOMATION,
      value: getRouteTitle(ROUTES.CONFIG_FIELD_AUTOMATION),
    },
    {
      id: ROUTES.UPLOAD_STATUS,
      value: getRouteTitle(ROUTES.UPLOAD_STATUS),
    },
    ...(flags.showGlSoiRecon
      ? [
          {
            id: ROUTES.ADMIN_PANEL_RECONCILIATION_MONITOR,
            value: getRouteTitle(ROUTES.ADMIN_PANEL_RECONCILIATION_MONITOR),
          },
        ]
      : []),
    ...(permSvc.hasPermission(PermissionKey.customerSuccessAll)
      ? [
          {
            id: ROUTES.ADMIN_PANEL_ATLAS_CONSOLE,
            value: getRouteTitle(ROUTES.ADMIN_PANEL_ATLAS_CONSOLE),
          },
          {
            id: ROUTES.PROCESSING,
            value: getRouteTitle(ROUTES.PROCESSING),
          },
        ]
      : []),
  ];

  const SOURCING_SECTIONS = [
    {
      id: ROUTES.CONFIG_SECTORS,
      value: getRouteTitle(ROUTES.CONFIG_SECTORS),
    },
  ];

  let portCoSections = PORTFOLIO_SECTIONS;
  let customizationSections = CUSTOMIZATION_SECTIONS;
  if (!flags.showGeneralLedger) {
    portCoSections = portCoSections.filter((sec) => sec.id !== ROUTES.GENERAL_LEDGER);
  }
  if (!permSvc.hasPermission(PermissionKey.customerSuccessAll)) {
    portCoSections = portCoSections.filter(
      (sec) =>
        sec.id !== ROUTES.BULK_DEAL_DOCS &&
        sec.id !== ROUTES.UPDATE_DEAL_DOCS_ROUND &&
        sec.id !== ROUTES.RESET_REINGESTION &&
        sec.id !== ROUTES.CONFIG_FIELD_AUTOMATION
    );
  }
  if (!permSvc.hasPermission(PermissionKey.customerSuccessAll)) {
    customizationSections = customizationSections.filter(
      (sec) =>
        sec.id !== ROUTES.ADMIN_PANEL_COLUMNS &&
        sec.id !== ROUTES.ADMIN_PANEL_FIELD_MANAGER &&
        sec.id !== ROUTES.ADMIN_PANEL_FORMATTERS
    );
  }
  // Routes for these two are registered only with client-settings (AppRoutes2), so
  // an item here without it would lead to a page that does not exist for the user.
  if (!permSvc.hasPermission(PermissionKey.canViewClientSettings)) {
    customizationSections = customizationSections.filter(
      (sec) => sec.id !== ROUTES.APPLICATION_SETTINGS && sec.id !== ROUTES.ADMIN_PANEL_DATA_COLLECTION
    );
  }
  if (!flags.showFieldsManager) {
    customizationSections = customizationSections.filter(
      (sec) => sec.id !== ROUTES.ADMIN_PANEL_FIELD_MANAGER
    );
  }
  if (!flags.showFormattersConfig) {
    customizationSections = customizationSections.filter((sec) => sec.id !== ROUTES.ADMIN_PANEL_FORMATTERS);
  }
  if (!flags.showCompanyMappings) {
    customizationSections = customizationSections.filter((sec) => sec.id !== ROUTES.ADMIN_PANEL_MAPPINGS);
  }
  if (!flags.showDataCollectionConfig) {
    customizationSections = customizationSections.filter(
      (sec) => sec.id !== ROUTES.ADMIN_PANEL_DATA_COLLECTION
    );
  }

  return (
    <NavigationWrapper title='Admin Panel'>
      {MANAGEMENT_SECTIONS.length > 0 && (
        <SubSectionNavigation title='Management'>
          <SubSectionNavItems
            sectionList={orderSections(MANAGEMENT_SECTIONS)}
            parentRoute={ROUTES.ADMIN_PANEL}
          />
        </SubSectionNavigation>
      )}
      <SubSectionNavigation title='CUSTOMIZATION'>
        <SubSectionNavItems
          sectionList={orderSections(customizationSections)}
          parentRoute={ROUTES.ADMIN_PANEL}
        />
      </SubSectionNavigation>
      {permSvc.hasPermission(PermissionKey.canEditDeal) && (
        <SubSectionNavigation title='Pipeline Configuration'>
          <SubSectionNavItems sectionList={orderSections(PIPELINE_SECTIONS)} parentRoute={CONFIG_ROUTE} />
        </SubSectionNavigation>
      )}
      {permSvc.hasPermission(PermissionKey.canEditPortfolioCompany) && (
        <SubSectionNavigation title='Portfolio Configuration'>
          <SubSectionNavItems sectionList={orderSections(portCoSections)} parentRoute={CONFIG_ROUTE} />
        </SubSectionNavigation>
      )}
      <SubSectionNavigation title='Sourcing Configuration'>
        <SubSectionNavItems sectionList={orderSections(SOURCING_SECTIONS)} parentRoute={CONFIG_ROUTE} />
      </SubSectionNavigation>
    </NavigationWrapper>
  );
};
